import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CopyButton } from "../components/CopyButton";
import { SCOPE_LABEL } from "../lib/scopes";
import { resetConnectedCount } from "../lib/useConnectedCount";
import { useMcpUrl } from "../lib/useMcpUrl";
import { relativeTime } from "../lib/time";
import { brand } from "../theme/brand.config";
import sparkleSvg from "../theme/icons/ai-sparkle.svg?raw";

type Grants = Awaited<ReturnType<typeof api.connectedApps>>["grants"];
type Grant = Grants[number];

// Server-assigned MCP scopes → human labels come from lib/scopes (shared
// with the API-key picker); the raw token stays in the chip's title for
// the technically inclined, and an unmapped scope falls back to raw.

// Worker error tokens → sentences a non-developer can act on. Rename and
// disconnect are role-gated server-side (canManageKeys), so a member hits
// `forbidden`; anything unrecognised falls back to the raw token.
function actionErrorMessage(e: unknown): string {
  const token = e instanceof Error ? e.message : String(e);
  if (token === "forbidden")
    return "Only account owners and admins can manage agent connections.";
  return `That didn’t go through (${token}) — try again, or reload the page.`;
}

// Setup instructions shared by the empty state (full page) and the
// "Connect another agent" disclosure shown once grants exist. Both
// clients hit the same OAuth-protected MCP endpoint; the URL comes
// from the worker's RFC 9728 metadata (useMcpUrl) so it can't drift
// from MCP_RESOURCE_URL.
function ConnectInstructions() {
  const mcpUrl = useMcpUrl();
  // The one-command path for Claude Code users — only rendered once the
  // real endpoint has resolved, so we never offer a copy button on a
  // placeholder command.
  // Quote the name: brand.productName is env-configurable (VITE_BRAND_PRODUCT)
  // and a value with a space would word-split into two CLI args when the
  // command is copied and run verbatim.
  const claudeCodeCmd = mcpUrl
    ? `claude mcp add --transport http "${brand.productName}" ${mcpUrl}`
    : null;
  return (
    <>
      <div className="card">
        <div className="eyebrow">MCP endpoint</div>
        <p className="muted" style={{ margin: "var(--space-2) 0 var(--space-3)" }}>
          Every agent connects to this one OAuth-protected URL — you sign in
          to this portal and approve the connection. No API key is involved.
        </p>
        <div className="endpoint">
          <code className="key">{mcpUrl ?? "Loading…"}</code>
          {mcpUrl && <CopyButton value={mcpUrl} />}
        </div>
      </div>

      <div className="provider-grid">
        <div className="card provider">
          <div className="provider-head">
            <span className="provider-badge">Claude</span>
          </div>
          <ol className="step-list">
            <li>
              In Claude (web or desktop), open <strong>Settings</strong> →{" "}
              <strong>Connectors</strong>.
            </li>
            <li>
              Click <strong>Add custom connector</strong>.
            </li>
            <li>
              Name it <code>{brand.productName}</code> and paste the endpoint
              URL above, then click <strong>Add</strong>.
            </li>
            <li>
              When prompted, sign in with your {brand.productName} account and
              approve the connection.
            </li>
          </ol>
          <div className="provider-foot muted">
            Or one command in Claude Code:
            {claudeCodeCmd ? (
              <div className="endpoint" style={{ marginTop: "var(--space-2)" }}>
                <code className="key">{claudeCodeCmd}</code>
                <CopyButton value={claudeCodeCmd} />
              </div>
            ) : (
              <>
                {" "}
                <code>claude mcp add --transport http "{brand.productName}" …</code>
              </>
            )}
          </div>
        </div>

        <div className="card provider">
          <div className="provider-head">
            <span className="provider-badge">ChatGPT</span>
            <span className="muted" style={{ fontSize: 12 }}>
              paid plan + developer mode
            </span>
          </div>
          <ol className="step-list">
            <li>
              Open <strong>Settings</strong> → <strong>Apps &amp;
              Connectors</strong> → <strong>Advanced settings</strong> and
              enable <strong>Developer mode</strong>.
            </li>
            <li>
              Back under <strong>Apps &amp; Connectors</strong>, click{" "}
              <strong>Create</strong>.
            </li>
            <li>
              Name it <code>{brand.productName}</code>, paste the endpoint URL
              above as the <strong>MCP server URL</strong>, and pick{" "}
              <strong>OAuth</strong>.
            </li>
            <li>
              Click <strong>Create</strong>, sign in with your{" "}
              {brand.productName} account, and approve.
            </li>
          </ol>
        </div>
      </div>

      <p className="muted" style={{ marginTop: 0, maxWidth: "64ch" }}>
        Once you approve, switch back to this tab — the agent appears in the
        list here automatically. Then try asking it something like{" "}
        <em>“find Marks and Spencer and summarise its latest filed
        accounts”</em>.
      </p>
    </>
  );
}

function AgentCard({
  grant,
  editing,
  confirming,
  onEdit,
  onEditChange,
  onSave,
  onCancel,
  onArmDisconnect,
  onCancelDisconnect,
  onDisconnect,
}: {
  grant: Grant;
  editing: boolean;
  confirming: boolean;
  onEdit: () => void;
  onEditChange: (name: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onArmDisconnect: () => void;
  onCancelDisconnect: () => void;
  onDisconnect: () => void;
}) {
  const initial = grant.client_name.trim().charAt(0).toUpperCase() || "A";
  return (
    <div className="entity-card">
      <span className="entity-avatar" aria-hidden>
        {initial}
      </span>
      <div className="entity-body">
        {editing ? (
          <input
            className="input"
            aria-label="Agent name"
            value={grant.client_name}
            autoFocus
            onChange={(e) => onEditChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSave();
              if (e.key === "Escape") onCancel();
            }}
          />
        ) : (
          <div className="entity-name">{grant.client_name}</div>
        )}
        <div className="entity-meta">
          {/* Same status treatment as the API-keys list, so "a live
              credential" reads identically on both pages. */}
          <span className="pill ok">
            <span className="pill-dot" aria-hidden />
            Active
          </span>
          <span title={new Date(grant.created_at).toLocaleString()}>
            Connected {relativeTime(grant.created_at)}
          </span>
        </div>
        <div className="chip-row">
          {grant.scopes.map((s) => (
            <span className="chip" key={s} title={s}>
              {SCOPE_LABEL[s] ?? s}
            </span>
          ))}
        </div>
      </div>
      <div className="entity-actions">
        {editing ? (
          <>
            <button
              className="btn"
              onClick={onSave}
              disabled={!grant.client_name.trim()}
            >
              Save
            </button>
            <button className="btn secondary" onClick={onCancel}>
              Cancel
            </button>
          </>
        ) : confirming ? (
          // Two-step disconnect (same pattern as API-key revoke):
          // disconnecting silently breaks a live agent integration, so the
          // first click arms a confirm rather than firing immediately.
          <>
            <button className="btn danger" onClick={onDisconnect}>
              Confirm disconnect
            </button>
            <button className="btn secondary" onClick={onCancelDisconnect}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button className="btn secondary" onClick={onEdit}>
              Rename
            </button>
            <button className="btn danger" onClick={onArmDisconnect}>
              Disconnect
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Sketch of the loaded list while the first fetch resolves — the blocks
// are decorative (aria-hidden); the sr-only line carries the spoken cue.
// Same convention as DashboardSkeleton.
function AgentsSkeleton() {
  return (
    <div role="status">
      <span className="sr-only">Loading connected agents…</span>
      <div className="entity-list" aria-hidden>
        {[0, 1].map((i) => (
          <div className="entity-card" key={i}>
            <span className="entity-avatar" style={{ background: "var(--color-surface-2)" }} />
            <div className="entity-body">
              <span className="skeleton" style={{ width: "30%" }} />
              <span
                className="skeleton"
                style={{ width: "45%", marginTop: "var(--space-3)" }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ConnectedApps() {
  // null = not yet fetched (same sentinel convention as Watchlists /
  // Billing / Usage). `error` is tracked separately so a failed fetch is
  // never mistaken for "no agents" — the empty state is an assertive
  // getting-started page, so showing it on a transient API error would
  // tell a user with connected agents that they have none.
  const [grants, setGrants] = useState<Grants | null>(null);
  const [error, setError] = useState(false);
  // Rename/disconnect failures (e.g. role-gated `forbidden`) surface here
  // instead of vanishing — a silent no-op reads as "the button is broken".
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [confirming, setConfirming] = useState<string | null>(null);
  // Last fetched grant count, so a refresh that changes it (agent
  // connected in another tab, or disconnected here) can nudge the sidebar
  // badge without an unconditional extra request per refresh.
  const lastCount = useRef<number | null>(null);
  // Guards the focus/visibility path below: a tab return fires both `focus`
  // and `visibilitychange`, so without this the two would kick off back-to-
  // back duplicate fetches. Set synchronously before the request so the
  // second event sees it in flight and skips.
  const refreshing = useRef(false);
  const refresh = () => {
    refreshing.current = true;
    return api
      .connectedApps()
      .then((r) => {
        setGrants(r.grants);
        setError(false);
        if (lastCount.current !== null && lastCount.current !== r.grants.length)
          resetConnectedCount();
        lastCount.current = r.grants.length;
      })
      .catch(() => setError(true))
      .finally(() => {
        refreshing.current = false;
      });
  };
  const saveLabel = () => {
    if (!editing || !editing.name.trim()) return;
    setActionErr(null);
    api
      .relabelApp(editing.id, editing.name.trim())
      .then(() => {
        setEditing(null);
        refresh();
      })
      .catch((e) => setActionErr(actionErrorMessage(e)));
  };
  const disconnect = (id: string) => {
    setConfirming(null);
    setActionErr(null);
    api
      .revokeApp(id)
      .then(() => refresh())
      .catch((e) => setActionErr(actionErrorMessage(e)));
  };
  useEffect(() => {
    refresh();
    // The connect flow ends in another tab (the agent's settings + OAuth
    // approval). Refetch when the user comes back so the new agent shows
    // up without a manual reload — this is what makes the "switch back to
    // this tab" line in the instructions true.
    const onFocus = () => {
      if (document.visibilityState === "visible" && !refreshing.current)
        void refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  let body: React.ReactNode;
  if (grants === null && error) {
    // Nothing fetched yet AND the fetch failed — a full-card retry is all
    // we can offer. Once a list has loaded, a failed background refresh
    // keeps the list and reports inline instead (below).
    body = (
      <div className="card">
        <p className="muted">
          Couldn’t load your connected agents — usually a transient
          network hiccup.
        </p>
        <button className="btn" onClick={refresh}>
          Retry
        </button>
      </div>
    );
  } else if (grants === null) {
    // Wait for the first fetch before choosing a layout — don't flash
    // the getting-started page at users who do have agents connected.
    body = <AgentsSkeleton />;
  } else if (grants.length === 0) {
    body = (
      <>
        <div className="agents-empty">
          <span
            className="hero-mark"
            aria-hidden
            dangerouslySetInnerHTML={{ __html: sparkleSvg }}
          />
          <h2>No agents connected yet</h2>
          <p className="muted">
            Connect Claude or ChatGPT and it can pull company data on this
            organisation’s behalf — every call is scoped, metered, and
            revocable here.
          </p>
        </div>
        <ConnectInstructions />
      </>
    );
  } else {
    const grant = (g: Grant) => (
      <AgentCard
        key={g.id}
        grant={editing?.id === g.id ? { ...g, client_name: editing.name } : g}
        editing={editing?.id === g.id}
        confirming={confirming === g.id}
        onEdit={() => {
          setConfirming(null);
          setEditing({ id: g.id, name: g.client_name });
        }}
        onEditChange={(name) => setEditing({ id: g.id, name })}
        onSave={saveLabel}
        onCancel={() => setEditing(null)}
        onArmDisconnect={() => setConfirming(g.id)}
        onCancelDisconnect={() => setConfirming(null)}
        onDisconnect={() => disconnect(g.id)}
      />
    );
    body = (
      <>
        {error && (
          <p className="error">
            Couldn’t refresh the list just now — showing the last loaded
            state.
          </p>
        )}
        {actionErr && <p className="error">{actionErr}</p>}
        <div className="entity-list">{grants.map(grant)}</div>
        <details className="connect-help">
          <summary>Connect another agent (Claude, ChatGPT)</summary>
          <ConnectInstructions />
        </details>
      </>
    );
  }

  return (
    <div className="main">
      <div className="page-header">
        <h1>Agents</h1>
        {grants && grants.length > 0 && (
          <span className="badge">
            {grants.length} connected
          </span>
        )}
      </div>
      {/* Hidden on the empty state, where the getting-started hero already
          says the same thing; shown while loading/erroring and once agents
          exist. */}
      {(grants === null || grants.length > 0) && (
        <p className="page-sub">
          AI agents you’ve authorised to pull company data on this
          organisation’s behalf via OAuth — no key to paste, and revoking here
          cuts access immediately. Calls count toward{" "}
          <Link to="/usage">Usage</Link>; to call the REST API from your own
          code, use an <Link to="/keys">API key</Link> instead.
        </p>
      )}
      {body}
    </div>
  );
}
